import { MediaResolutionInput, ResolvedMedia } from '../types';
import { toAbsoluteUrl } from './htmlMeta';

const YOUTUBE_VIDEO_ID_PATTERN =
  /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;

const TWITTER_STATUS_PATTERN = /(?:twitter\.com|x\.com)\/[^/]+\/status\/(\d+)/i;

const REDDIT_POST_PATTERN = /reddit\.com\/r\/([^/]+)\/comments\/([^/]+)/i;

export const parseRedditPostPath = (
  pageUrl: string
): {
  subreddit: string;
  postId: string;
} | null => {
  const match = pageUrl.match(REDDIT_POST_PATTERN);

  if (!match?.[1] || !match?.[2]) {
    return null;
  }

  return {
    subreddit: match[1],
    postId: match[2],
  };
};

const buildRedditEmbedUrl = (subreddit: string, postId: string): string =>
  `https://www.redditmedia.com/r/${subreddit}/comments/${postId}/?embed=true&ref_source=embed&ref=embed&showmedia=true`;

const toRedditJsonUrl = (pageUrl: string): string => {
  const url = new URL(pageUrl);

  url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
  url.search = '';
  url.hash = '';

  return url.toString();
};

type RedditPostData = {
  title?: string;
  thumbnail?: string;
  secure_media?: {
    reddit_video?: {
      fallback_url?: string;
    };
  };
  media?: {
    reddit_video?: {
      fallback_url?: string;
    };
  };
  preview?: {
    images?: Array<{
      source?: {
        url?: string;
      };
    }>;
  };
};

const extractRedditVideoUrl = (post: RedditPostData | undefined): string | null =>
  post?.secure_media?.reddit_video?.fallback_url ?? post?.media?.reddit_video?.fallback_url ?? null;

const extractRedditThumbnailUrl = (
  post: RedditPostData | undefined,
  input: MediaResolutionInput
): string | undefined => {
  const previewUrl = post?.preview?.images?.[0]?.source?.url?.replace(/&amp;/g, '&');
  const thumbnail = post?.thumbnail?.startsWith('http') ? post.thumbnail : null;

  return previewUrl ?? thumbnail ?? input.thumbnailUrl ?? undefined;
};

const buildYouTubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;

const buildYouTubeThumbnailUrl = (videoId: string): string =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

const normalizeEmbedUrl = (embedUrl: string, pageUrl: string): string => {
  const absoluteEmbedUrl = toAbsoluteUrl(embedUrl, pageUrl);
  const youtubeMatch = absoluteEmbedUrl.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/i);

  if (youtubeMatch?.[1]) {
    return buildYouTubeEmbedUrl(youtubeMatch[1]);
  }

  return absoluteEmbedUrl;
};

export const buildFallbackEmbedUrl = (pageUrl: string): string | null => {
  const youtubeMatch = pageUrl.match(YOUTUBE_VIDEO_ID_PATTERN);

  if (youtubeMatch?.[1]) {
    return buildYouTubeEmbedUrl(youtubeMatch[1]);
  }

  const tweetMatch = pageUrl.match(TWITTER_STATUS_PATTERN);

  if (tweetMatch?.[1]) {
    return `https://platform.twitter.com/embed/Tweet.html?id=${tweetMatch[1]}&theme=light&dnt=true`;
  }

  return null;
};

const buildFallbackThumbnailUrl = (pageUrl: string): string | undefined => {
  const youtubeMatch = pageUrl.match(YOUTUBE_VIDEO_ID_PATTERN);

  return youtubeMatch?.[1] ? buildYouTubeThumbnailUrl(youtubeMatch[1]) : undefined;
};

export const buildEmbedResolvedMedia = (
  embedUrl: string,
  pageUrl: string,
  input: MediaResolutionInput,
  metadata: ResolvedMedia['metadata'] = {},
  thumbnailUrl?: string | null
): ResolvedMedia => ({
  mediaType: 'video',
  playerType: 'iframe',
  embedUrl: normalizeEmbedUrl(embedUrl, pageUrl),
  thumbnailUrl:
    thumbnailUrl ?? input.thumbnailUrl ?? buildFallbackThumbnailUrl(pageUrl) ?? undefined,
  title: input.title ?? undefined,
  description: input.description ?? undefined,
  resolved: true,
  resolverName: 'OEmbedResolver',
  metadata,
});

export const resolveRedditMedia = async (
  pageUrl: string,
  input: MediaResolutionInput
): Promise<ResolvedMedia | null> => {
  const postPath = parseRedditPostPath(pageUrl);

  if (!postPath) {
    return null;
  }

  try {
    const response = await fetch(toRedditJsonUrl(pageUrl), {
      headers: {
        Accept: 'application/json',
        'User-Agent': 'Mozilla/5.0 (compatible; HozintMediaResolver/1.0)',
      },
    });

    if (response.ok) {
      const payload = (await response.json()) as Array<{
        data?: {
          children?: Array<{
            data?: RedditPostData;
          }>;
        };
      }>;
      const post = payload?.[0]?.data?.children?.[0]?.data;
      const playbackUrl = extractRedditVideoUrl(post);

      if (playbackUrl) {
        return {
          mediaType: 'video',
          playerType: 'html5',
          playbackUrl,
          thumbnailUrl: extractRedditThumbnailUrl(post, input),
          title: post?.title ?? input.title ?? undefined,
          description: input.description ?? undefined,
          resolved: true,
          resolverName: 'OEmbedResolver',
          metadata: {
            provider: 'reddit',
            strategy: 'reddit-json-video',
          },
        };
      }
    }
  } catch {
    // fall through to redditmedia embed
  }

  return buildEmbedResolvedMedia(
    buildRedditEmbedUrl(postPath.subreddit, postPath.postId),
    pageUrl,
    input,
    {
      provider: 'reddit',
      strategy: 'redditmedia-embed',
    }
  );
};
